import React, { useState } from "react";
import { Copy, KeyRound, RefreshCcw, ShieldCheck } from "lucide-react";
import { API_BASE_URL } from "./api";
import { Button, Field, MetricCard, SectionHeader, SurfaceCard } from "./components/designSystem";

export default function AdminToken() {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");

  const handleGenerate = async (event) => {
    event.preventDefault();
    setError("");
    setToken("");
    setLoading(true);

    try {
      const userRes = await fetch(`${API_BASE_URL}/api/find-user?email=${encodeURIComponent(email)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });
      const userData = await userRes.json();
      if (!userData.user || !userData.user._id) {
        setError("User not found");
        setLoading(false);
        return;
      }

      const generatedToken = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      setToken(generatedToken);

      const res = await fetch(`${API_BASE_URL}/api/admin/set-api-token`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({ userId: userData.user._id, token: generatedToken, expiresAt })
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || "Failed to set token");
        setToken("");
      }
    } catch {
      setError("Request failed");
      setToken("");
    }

    setLoading(false);
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopyMessage("Token copied.");
    } catch {
      setCopyMessage("Copy failed.");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_22%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.12),_transparent_22%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Admin Token"
          title="Generate user API tokens"
          description="Create a time-bound token for a user account and deliver it through your approved admin workflow."
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Token type" value="Manual" helper="Generated directly by admin." />
          <MetricCard label="Expiry" value={expiresAt ? "Configured" : "Pending"} helper="The token remains time-bound." />
          <MetricCard label="Delivery" value="Copy" helper="Send it through a trusted support channel." />
        </div>
      </SurfaceCard>

      {error ? <SurfaceCard><p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p></SurfaceCard> : null}
      {copyMessage ? <SurfaceCard><p className="text-sm font-medium text-sky-700 dark:text-sky-300">{copyMessage}</p></SurfaceCard> : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader eyebrow="Generate" title="Create a new token" description="Find the user by email, generate the token, set its expiry, and save it through the admin endpoint." />
          <form onSubmit={handleGenerate} className="mt-6 space-y-5">
            <Field label="User Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" required />
            <Field label="Expiration Date & Time" type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} required />
            <div className="flex flex-wrap gap-3">
              <Button type="submit" variant="brand" disabled={loading}>
                <KeyRound className="h-4 w-4" />
                {loading ? "Generating..." : "Generate Token"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => { setEmail(""); setExpiresAt(""); setToken(""); setError(""); setCopyMessage(""); }}>
                <RefreshCcw className="h-4 w-4" />
                Reset
              </Button>
            </div>
          </form>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader eyebrow="Output" title="Generated token" description="Copy the token immediately and deliver it to the user through your support process." />
          {token ? (
            <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">Token ready</p>
                  <pre className="mt-4 overflow-x-auto rounded-2xl bg-white p-4 text-xs text-slate-700 dark:bg-gray-800 dark:text-slate-200">{token}</pre>
                  <p className="mt-4 text-xs leading-6 text-slate-500 dark:text-slate-400">Copy and send this token to the user via email or your chosen admin support channel.</p>
                </div>
                <Button type="button" variant="secondary" onClick={copyToken}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
              Generate a token to see it here.
            </div>
          )}
          <div className="mt-6 flex items-start gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <ShieldCheck className="mt-1 h-5 w-5 text-sky-600 dark:text-sky-300" />
            <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">Treat admin-generated tokens as sensitive credentials and only share them over channels you already trust operationally.</p>
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}
